<template>
  <div class="delivery-new">
    <template v-if="!store.loading && !store.error">
      <v-container>
        <v-form v-model="formValid" :ref="form">
          <v-text-field
            v-model="LieferscheinNr"
            label="Lieferscheinnummer"
            :rules="[ruleRequired]"
            maxlength="10"
            aria-autocomplete="none"
            autocomplete="off"
            required
            autofocus
          />
          <v-autocomplete
            v-model="lieferant"
            :disabled="Boolean(store.data)"
            :items="lieferantItems"
            item-title="text"
            label="Lieferant"
            :rules="[ruleRequired]"
            v-model:search="lieferantSword"
            :loading="lieferantenLoading"
            :return-object="true"
            required
          />
          <v-text-field
            v-if="BestellNr"
            v-model="BestellNr"
            disabled
            :items="bestellItems"
            :rules="[ruleNumber]"
            label="Bestellnummer"
          />
          <date-picker
            class="mt-5"
            v-model="Lieferdatum"
            label="Lieferdatum"
            :rules="[ruleRequired]"
          />
          <v-btn color="primary" @click="save" :disabled="!formValid">Erstellen</v-btn>
        </v-form>
        <v-card v-if="order" class="mt-10">
          <v-card-title class="py-3">Bestellung</v-card-title>
          <v-list class="pt-0 pb-3">
            <v-list-item v-for="position in order.BestellungPosition" :key="position.PosNr">
              <template #prepend>
                <div class="mr-3">
                  {{ position.PosNr }}
                </div>
              </template>

                <v-list-item-title>
                  {{ position.Artikel.ArtikelBezeichnung1 }}
                  <small class="text-grey">#{{ position.Artikel.ArtikelNr }}</small>
                </v-list-item-title>
                <v-list-item-subtitle>{{ position.Artikel.ArtikelBezeichnung2 }}</v-list-item-subtitle>
                <v-list-item-subtitle></v-list-item-subtitle>
                <v-list-item-subtitle></v-list-item-subtitle>
                <v-list-item-subtitle>
                  {{ position.Einheit.Menge }} {{ position.Einheit.Einheit }}
                  <template v-if="position.Artikel.ArtikelNrLieferant">&ndash; {{ position.Artikel.ArtikelNrLieferant }}</template>
                </v-list-item-subtitle>

            </v-list-item>
          </v-list>
        </v-card>
      </v-container>
    </template>
    <template v-else-if="store.error">
      <error :module="store.$id" />
    </template>
    <template v-else>
      <loading />
    </template>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, ref, watch} from 'vue';
import Error from '@/components/Error.vue';
import Loading from '@/components/Loading.vue';
import Picklist, {ApiPicklistState, ApiPicklistType} from '@/model/Picklist';
import {ProviderStatus} from '@/model/Provider';
import Provider from '@/model/Provider';
import Order, {DetailOrderFilters} from '@/model/Order';
import router from '@/router';
import DatePicker from '@/components/DatePicker.vue';
import moment from 'moment';
import {doAlert} from '@/util';
import {getProviders} from '@/api/module/provider';
import {ValueType} from '@/api/meta/filters';
import {useStorage} from '@/composables/storage';
import useDetailPicklistStore from '@/store/detailPicklist';
import {VNodeRef} from 'vue';
import useDetailOrderStore from '@/store/detailCrmOrder';
const store = useDetailOrderStore();
const detailPicklist = useDetailPicklistStore();
useStorage(store,
    () => null,
    (route): DetailOrderFilters => {
      return {Nummer: parseInt(Array.isArray(route.params.id) ? route.params.id[0] : route.params.id)};
      // return {Nummer: parseInt(Array.isArray(route.params.id) ? route.params.id[0] : route.params.id)};
    },
);

const lieferant = ref<{value: string; text: string, name: string} | null>(null);
const BestellNr = ref('');
const Lieferdatum = ref(moment().format('YYYY-MM-DD'));
const LieferscheinNr = ref('');
const formValid = ref(false);
const lieferantenLoaded = ref(false);
const lieferanten = ref<Provider[]>([]);
const debounceLieferantenTimeout = ref<number | null>(null);
const lieferantSword = ref('');
const lieferantenLoading = ref(false);
const form = ref<HTMLFormElement & VNodeRef | undefined>();

const lieferantItems = computed(() => {
  const lieferantenOptions = lieferanten.value.map((l) => ({value: String(l.KundenNr), text: `${l.Kundname} [${l.KundenNr}]`, name: l.Kundname}));

  if (lieferant.value) {
    lieferantenOptions.unshift(lieferant.value);
  }

  const data = store.data;


  return data ?
    [
      {value: String(data?.Lieferant.LieferantNr), text: `${data?.Lieferant.LieferantName} [${data?.Lieferant.LieferantNr}]`, name: data?.Lieferant.LieferantName},
      ...lieferantenOptions,
    ] :
    lieferantenOptions;
});
const bestellItems = computed(() => {
  const order = store.data;
  return order ? [{value: order?.Nummer, text: order?.Nummer}] : [];
});
const order = computed(() => {
  return store.data;
});

function ruleRequired(val: string|null|undefined): string|true {
  if (!val) return 'erforderlich';

  return true;
}
function ruleNumber(val: string|null|undefined): string|true {
  if (!val) return true;
  if (parseInt(val).toString() !== val) return 'bitte eine ganze Zahl eingeben';

  return true;
}
async function loadLieferanten() {
  if (!lieferantSword.value) {
    return;
  }
  lieferantenLoading.value = true;
  try {
    [lieferanten.value] = await getProviders({
      KundStatus$In: [ProviderStatus.Lieferanten],
      LieferantErledigt: false,
      Fulltext$Fulltext: {
        words: [String(lieferantSword.value)],
        fields: [
          {path: ['KundenNr'], valueType: ValueType.Naked},
          {path: ['Kundname'], valueType: ValueType.StringContains},
        ],
      },
    }, []);
  } finally {
    lieferantenLoading.value = false;
  }
}

async function save() {
  try {
    const order = store.data as Order | null;
    form.value?.validate();
    if (!formValid.value) throw new Error('Bitte alle Felder korrekt ausfüllen');
    const picklist = Picklist.fromApi({
      Art: ApiPicklistType.Incoming,
      Picklistnr: LieferscheinNr.value,
      BestellNr: BestellNr.value ? parseInt(BestellNr.value) : null,
      LieferantenNr: lieferant.value !== null ? Number(lieferant.value.value) : null,
      Erstelltam: moment().format('YYYY-MM-DD'),
      Erstelltdurch: '',
      Status: ApiPicklistState.Todo,
      Kmsdatum: order?.Datum ?? '',
      Kmsuhrzeit: order?.Uhrzeit ?? '',
      Kmsprioritaet: '',
      Kmsam: Lieferdatum.value,
      Kmsdurch: '',
      Kmsbediener: null,
      Tour: [],
      Auftraege: [],
      InterneAuftraege: order?.convertedPositions ?? [],
    });

    picklist.lieferantName = lieferant.value?.name;

    detailPicklist.data = picklist;

    router.push({name: 'order_pick'}).catch(console.error);
  } catch (err: any) {
    await doAlert(err.message, 'Fehler');
  }
}

watch(() => store.data, async (data) => {
  if (data !== null) {
    lieferant.value = {value: String(data.Lieferant.LieferantNr), text: `${data.Lieferant.LieferantName} [${data.Lieferant.LieferantNr}]`, name: data.Lieferant.LieferantName};
    BestellNr.value = data.Nummer.toString();
    Lieferdatum.value = data.DatumParsed.format('YYYY-MM-DD');
    console.log(data);
  }

  await nextTick();
  form.value?.validate();
});

watch(lieferantSword, () => {
  if (debounceLieferantenTimeout.value) clearTimeout(debounceLieferantenTimeout.value);
  debounceLieferantenTimeout.value = setTimeout(loadLieferanten, 500);
});

watch(form, () => {
  form.value?.validate();
});

defineExpose({
  Error, Loading, DatePicker,
  lieferant,
  BestellNr,
  Lieferdatum,
  LieferscheinNr,
  formValid,
  lieferantenLoaded,
  lieferanten,
  debounceLieferantenTimeout,
  lieferantSword,
  lieferantenLoading,
  form,
  lieferantItems,
  bestellItems,
  order,
  save,
  ruleRequired,
  ruleNumber,
});

</script>

<style>
.item-operator-info .v-chip__content {
  display: flex;
  justify-content: center;
}
.v-pagination__navigation, .v-pagination__item--active {
  box-shadow: none;
}
</style>
